import i18n, { InitOptions } from "i18next";
import { initReactI18next } from "react-i18next";
import Languages from "./languages";
import { detectHmo } from "utils/hmoDetection";
import { deepMerge } from "utils/deepMerge";
import { DEFAULT_LANGUAGE, resolveInitialLanguage } from "utils/language.util";

/**
 * Builds translations for a specific HMO by merging HMO-specific overrides into base translations
 * @param baseTranslations - Base translation object
 * @param hmo - HMO identifier (e.g., 'leumit', 'clalit', etc.)
 * @returns Merged translations with HMO overrides applied
 */
function buildTranslationsForHmo(
  baseTranslations: any,
  hmo: string | null
): any {
  if (!hmo || !baseTranslations.hmo?.[hmo]) {
    // No HMO detected or no overrides for this HMO - return base translations (excluding hmo section)
    const { hmo: _, ...rest } = baseTranslations;
    return rest;
  }

  // Extract HMO overrides and base translations
  const { hmo: hmoOverrides, ...base } = baseTranslations;
  const hmoOverride = hmoOverrides[hmo];

  // Deep merge HMO overrides into base translations
  return deepMerge(base, hmoOverride);
}

// Detect HMO at initialization time
const detectedHmo = detectHmo();

// Build resources with HMO-specific translations merged
const resources = Languages.reduce(
  (prev, curr) => {
    const mergedTranslations = buildTranslationsForHmo(curr.source, detectedHmo);
    return {
      ...prev,
      [curr.key]: {
        translation: mergedTranslations,
      },
    };
  },
  {} as Record<string, { translation: any }>
);

i18n.use(initReactI18next).init({
  lng: resolveInitialLanguage(),
  fallbackLng: DEFAULT_LANGUAGE,
  resources,
  compatibilityJSON: "v4", // v25 requires v4
  defaultNS: "translation",
  keySeparator: ":",
  nsSeparator: false,
  interpolation: {
    escapeValue: false,
  },
} as InitOptions);

export default i18n;
